// src/pages/Billing.jsx
// Control Panel → Billing (Super Admins only; never part of Roles & Access).
// Pick an account from the list to see and change its rates (3.9: one at a
// time), used by the Usage tab and the monthly record:
//   Business accounts  tiered rate by total pages in the period, the page
//                      rules for faxes without a page count, and the
//                      month-end adjustment of the recorded total
//   Individual accounts one flat multiplier
// Also shows the account's fax lines and the address people email faxes to.
// New client accounts will show up here once they can be created.
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotify } from '../components/Notifications';
import { fetchBilling, saveBilling, fetchCyclePreview } from '../utils/api';
import Loader from '../components/Loader';
import Emo from '../components/Emo';

const fmtNum = n => Number(n).toLocaleString('en-US');
const fmtFax = num => {
  const n = String(num || '').replace(/\D/g, '');
  return n.length === 11 ? `+${n[0]} (${n.slice(1, 4)}) ${n.slice(4, 7)}-${n.slice(7)}` : num;
};

const PICK_KEY = 'faxPortal.billingAccount';

export default function Billing() {
  const { getToken } = useAuth();
  const [accounts, setAccounts] = useState(null);
  const [error, setError] = useState('');
  const [picked, setPicked] = useState(() => { try { return window.localStorage.getItem(PICK_KEY) || ''; } catch { return ''; } });
  const [dirty, setDirty] = useState(false);
  const { confirm } = useNotify();

  const load = useCallback(async () => {
    setError('');
    try { setAccounts((await fetchBilling(getToken)).accounts || []); } catch (e) { setError(e.message); }
  }, [getToken]);
  useEffect(() => { load(); }, [load]);

  const current = (accounts || []).find(a => a.id === picked) || (accounts || [])[0] || null;
  // 3.67.7 (owner): the portal's own dialog, not the browser's
  async function pick(id) {
    if (dirty && !(await confirm({ title: 'Leave without saving?', message: "Your changes to this client's billing aren't saved.", confirmLabel: 'Leave without saving', cancelLabel: 'Keep editing', danger: true }))) return;
    setDirty(false); setPicked(id);
    try { window.localStorage.setItem(PICK_KEY, id); } catch { /* ignore */ }
  }

  return (
    <div>
      <h3 style={S.title}>Billing</h3>
      <p style={S.sub}>Rates and billing cycle for each client. The Usage tab and the monthly record use them. Only Super Admins see this page.</p>
      <p style={S.sub}><Emo e="🔒" /> <strong>Rate changes apply to the current month and later, for that client only; a new billing cycle starts when the open period ends.</strong> Once a month closes (at the end of its billing period), its numbers are recorded and never change, whatever is changed here afterwards.</p>
      {error && <div role="alert" style={S.error}>{error}</div>}
      {!accounts && !error && <Loader label="Loading billing…" />}
      {accounts && (
        <div style={S.picker}>
          <label htmlFor="billing-account" style={S.pickerLabel}>Client</label>
          <select id="billing-account" value={current?.id || ''} onChange={e => pick(e.target.value)} style={S.select}>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.type === 'individual' ? 'Individual' : 'Business'})</option>)}
          </select>
          <span style={S.muted}>{accounts.length} {accounts.length === 1 ? 'client' : 'clients'}</span>
        </div>
      )}
      {current && <AccountCard key={current.id} account={current} onSaved={load} onDirty={setDirty} />}
    </div>
  );
}

function AccountCard({ account, onSaved, onDirty }) {
  const { getToken } = useAuth();
  const { notify } = useNotify();
  const individual = account.type === 'individual';
  const [draft, setDraft] = useState(() => toDraft(account.billing));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  // Billing cycle (3.13.0): the start day in effect, or the one coming
  const cycle = account.cycle || { day: 25, pending: null, open: null };
  // 3.67.3 (owner): and the end day: the day before the next start, or the
  // next start day itself, counted in both periods (Clinicmind's 25th to 25th)
  const cycleBase = String(cycle.pending?.day ?? cycle.day);
  const ovBase = !!(cycle.pending ? cycle.pending.overlap : cycle.overlap);
  const [cycleDay, setCycleDay] = useState(cycleBase);
  const [cycleOv, setCycleOv] = useState(ovBase);
  const cycleDirty = cycleDay !== cycleBase || cycleOv !== ovBase;
  const [cyclePlan, setCyclePlan] = useState(null);
  useEffect(() => {
    if (!cycleDirty) { setCyclePlan(null); return; }
    let live = true;
    fetchCyclePreview(account.id, cycleDay, getToken, cycleOv).then(r => { if (live) setCyclePlan(r.success ? r : null); }).catch(() => {});
    return () => { live = false; };
  }, [cycleDay, cycleOv, cycleDirty, account.id, getToken]);
  const dirty = cycleDirty || JSON.stringify(draft) !== JSON.stringify(toDraft(account.billing));
  useEffect(() => { onDirty(dirty); }, [dirty, onDirty]);

  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const setTier = (i, k, v) => setDraft(d => ({ ...d, tiers: d.tiers.map((t, j) => (j === i ? { ...t, [k]: v } : t)) }));
  const addTier = () => setDraft(d => {
    const tiers = [...d.tiers];
    const prevUpTo = Number(tiers[tiers.length - 2]?.upTo) || 0;
    tiers.splice(tiers.length - 1, 0, { upTo: String(prevUpTo + 1000), rate: tiers[tiers.length - 1]?.rate || '1' });
    return { ...d, tiers };
  });
  const removeTier = i => setDraft(d => ({ ...d, tiers: d.tiers.filter((_, j) => j !== i) }));

  async function save() {
    setSaving(true); setErr('');
    try {
      const billing = individual
        ? { multiplier: Number(draft.multiplier), zeroPages: Number(draft.zeroPages) }
        : {
            tiers: draft.tiers.map((t, i) => ({ upTo: i === draft.tiers.length - 1 ? null : Number(t.upTo), rate: Number(t.rate) })),
            unknownOutgoingPages: Number(draft.unknownOutgoingPages),
            failedOutgoingMinPages: Number(draft.failedOutgoingMinPages),
            zeroIncomingPages: Number(draft.zeroIncomingPages),
            monthEnd: { on: draft.meOn, min: Number(draft.meMin), max: Number(draft.meMax), under: Number(draft.meUnder) },
          };
      await saveBilling(account.id, billing, getToken, cycleDirty ? Number(cycleDay) : undefined, cycleOv);
      notify({ tone: 'success', title: `Billing saved for ${account.name}` });
      onSaved();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  }

  return (
    <section style={S.card} aria-label={`${account.name} billing`}>
      <div style={S.cardHead}>
        <div>
          <div style={S.name}>{account.name} <span style={individual ? S.badgeInd : S.badgeBiz}>{individual ? 'Individual' : 'Business'}</span></div>
          <div style={S.meta}>
            Faxes by email to <strong>{account.emailAddress || '—'}</strong>
            {account.domains.length > 0 && <> · People from {account.domains.join(', ')}</>}
          </div>
        </div>
      </div>

      <div style={S.lines}>
        <div style={S.sectionLabel}>Fax lines</div>
        {account.numbers.length
          ? account.numbers.map(n => <div key={n.number} style={S.line}>{fmtFax(n.number)} <span style={S.lineLabel}>{n.label}</span></div>)
          : <div style={S.muted}>No lines</div>}
      </div>

      <div style={S.lines}>
        <div style={S.sectionLabel}>Billing cycle</div>
        <div style={S.cycleRow}>
          <label style={S.cycleLabel}>Each period starts on day
            <select value={cycleDay} onChange={e => setCycleDay(e.target.value)} style={{ ...S.input, width: 80 }}>
              {Array.from({ length: 28 }, (_, i) => String(i + 1)).map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            of the month
          </label>
          <label style={S.cycleLabel}>and ends
            <select value={cycleOv ? 'same' : 'before'} onChange={e => setCycleOv(e.target.value === 'same')} style={{ ...S.input, width: 'auto', maxWidth: '100%' }}>
              <option value="before">the day before the next start ({endBefore(cycleDay)})</option>
              <option value="same">on the next start day, counted in both ({ordinal(cycleDay)} to {ordinal(cycleDay)})</option>
            </select>
          </label>
          {cycle.open && (
            <span style={S.muted}>
              Open period: <strong>{fmtMonth(cycle.open.key)}</strong> ({fmtDay(cycle.open.start)} – {fmtDay(cycle.open.lastDay)})
            </span>
          )}
        </div>
        {cycle.pending && !cycleDirty && (
          <p style={S.hint}>Changes to day <strong>{cycle.pending.day}</strong>{cycle.pending.overlap ? ', both days counted,' : ''} from <strong>{fmtDay(cycle.pending.from)}</strong>, when the open period ends.</p>
        )}
        {cycleDirty && (
          <p style={S.cycleNote}>
            {cyclePlan?.effective
              ? <>Takes effect <strong>{fmtDay(cyclePlan.effective)}</strong>, when the open period ends; the open period keeps its dates. Next periods: </>
              : <>Back to the day in effect now (the change still to come is cancelled). Periods: </>}
            {(cyclePlan?.preview || []).map((p, i) => <span key={p.key}>{i ? ' · ' : ''}<strong>{fmtMonth(p.key)}</strong> {fmtDay(p.start)} – {fmtDay(p.lastDay)}</span>)}
          </p>
        )}
        <p style={S.hint}><Emo e="🔒" /> Closed months are recorded and never change. Rate changes apply to the current month and later; a new cycle starts when the open period ends.</p>
      </div>

      {individual ? (
        <div style={S.grid}>
          <Field label="Multiplier" hint="Every page and fax count is multiplied by this">
            <input type="number" step="0.01" min="0.01" value={draft.multiplier} onChange={e => set('multiplier', e.target.value)} style={S.input} />
          </Field>
          <Field label="Pages for a fax with no page count">
            <input type="number" min="0" value={draft.zeroPages} onChange={e => set('zeroPages', e.target.value)} style={S.input} />
          </Field>
        </div>
      ) : (
        <>
          <div style={S.sectionLabel}>Rate by total pages in the period</div>
          <table style={S.table}>
            <thead><tr><th style={S.th}>Total pages</th><th style={S.th}>Rate</th><th style={S.th} aria-label="Remove" /></tr></thead>
            <tbody>
              {draft.tiers.map((t, i) => {
                const last = i === draft.tiers.length - 1;
                const from = i === 0 ? 0 : Number(draft.tiers[i - 1].upTo) || 0;
                return (
                  <tr key={i}>
                    <td style={S.td}>
                      {last ? <span>{fmtNum(from)} and above</span> : (
                        <span>{i === 0 ? 'Under ' : `${fmtNum(from)} to under `}
                          <input type="number" min="1" value={t.upTo} onChange={e => setTier(i, 'upTo', e.target.value)} style={{ ...S.input, width: 110 }} aria-label={`Tier ${i + 1} up to`} />
                        </span>
                      )}
                    </td>
                    <td style={S.td}>
                      <input type="number" step="0.001" min="0.01" value={t.rate} onChange={e => setTier(i, 'rate', e.target.value)} style={{ ...S.input, width: 90 }} aria-label={`Tier ${i + 1} rate`} /> ×
                    </td>
                    <td style={S.td}>
                      {!last && draft.tiers.length > 2 && <button onClick={() => removeTier(i)} style={S.linkBtn} aria-label={`Remove tier ${i + 1}`}>Remove</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <button onClick={addTier} style={S.linkBtn}>+ Add a tier</button>
          <div style={S.grid}>
            <Field label="Sent fax with no page count" hint="Billed as this many pages">
              <input type="number" min="0" value={draft.unknownOutgoingPages} onChange={e => set('unknownOutgoingPages', e.target.value)} style={S.input} />
            </Field>
            <Field label="Failed sent fax, at least" hint="First sends and resends; automatic retries bill actual pages">
              <input type="number" min="0" value={draft.failedOutgoingMinPages} onChange={e => set('failedOutgoingMinPages', e.target.value)} style={S.input} />
            </Field>
            <Field label="Failed received fax with no pages" hint="Billed as this many pages">
              <input type="number" min="0" value={draft.zeroIncomingPages} onChange={e => set('zeroIncomingPages', e.target.value)} style={S.input} />
            </Field>
          </div>

          <div style={S.meBox}>
            <label style={S.meToggle}>
              <input type="checkbox" checked={draft.meOn} onChange={e => set('meOn', e.target.checked)} />
              <span><strong>Month-end adjustment</strong> of the recorded page total</span>
            </label>
            <p style={S.hint}>
              When the month is recorded (after the rate above), the page total becomes the same thousand plus a random number between the two below.
              {' '}For example, with {draft.meMin || '…'} to {draft.meMax || '…'}, a total of 28,500 is recorded as {meExample(draft)}.
              {' '}A total whose last three digits are {draft.meUnder || '…'} or less stays exact (for example {fmtNum(28000 + Math.min(999, Number(draft.meUnder) || 0))}), and a total never goes below its real number (28,950 becomes 28,950 to {fmtNum(28000 + (Number(draft.meMax) || 0))}). The Usage tab's month-to-date figures don't include it.
            </p>
            {draft.meOn && (
              <div style={S.grid}>
                <Field label="From" hint="Added to the thousand (0 to 999)">
                  <input type="number" min="0" max="999" value={draft.meMin} onChange={e => set('meMin', e.target.value)} style={S.input} />
                </Field>
                <Field label="To" hint="At least the first number (up to 1999)">
                  <input type="number" min="0" max="1999" value={draft.meMax} onChange={e => set('meMax', e.target.value)} style={S.input} />
                </Field>
                <Field label="Leave exact up to" hint="When the last three digits are this or less (0 to 999)">
                  <input type="number" min="0" max="999" value={draft.meUnder} onChange={e => set('meUnder', e.target.value)} style={S.input} />
                </Field>
              </div>
            )}
          </div>
        </>
      )}

      {err && <div role="alert" style={S.error}>{err}</div>}
      <div style={S.actions}>
        {dirty && <button onClick={() => { setDraft(toDraft(account.billing)); setCycleDay(cycleBase); setErr(''); }} style={S.cancelBtn} disabled={saving}>Undo changes</button>}
        <button onClick={save} disabled={!dirty || saving} style={{ ...S.saveBtn, opacity: !dirty || saving ? 0.5 : 1 }}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </section>
  );
}

function Field({ label, hint, children }) {
  return (
    <label style={S.field}>
      <span style={S.fieldLabel}>{label}</span>
      {children}
      {hint && <span style={S.hint}>{hint}</span>}
    </label>
  );
}

// "October 2026" and "Oct 25, 2026" (billing cycle, UTC dates)
function fmtMonth(key) {
  const [y, m] = String(key).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}
// The next day (Clinicmind's periods include the end 25th)
// 3.67.3: "1st", "25th"; the last day of a cycle that ends the day before
// the next start ("the 24th", or "the end of the month" for day 1)
function ordinal(d) {
  const n = Number(d), t = n % 100;
  return `${n}${t >= 11 && t <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] || 'th'}`;
}
const endBefore = d => (Number(d) === 1 ? 'the end of the month' : `${ordinal(d)} to ${ordinal(Number(d) - 1)}`);
function fmtDay(iso) {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function toDraft(b = {}) {
  return {
    multiplier: String(b.multiplier ?? ''), zeroPages: String(b.zeroPages ?? ''),
    tiers: (b.tiers || []).map(t => ({ upTo: t.upTo == null ? '' : String(t.upTo), rate: String(t.rate) })),
    unknownOutgoingPages: String(b.unknownOutgoingPages ?? ''), failedOutgoingMinPages: String(b.failedOutgoingMinPages ?? ''),
    zeroIncomingPages: String(b.zeroIncomingPages ?? ''),
    meOn: b.monthEnd ? !!b.monthEnd.on : false,
    meMin: String(b.monthEnd?.min ?? 850), meMax: String(b.monthEnd?.max ?? 1010), meUnder: String(b.monthEnd?.under ?? 100),
  };
}

// "28,850 to 29,010" for the hint
function meExample(d) {
  const lo = Number(d.meMin), hi = Number(d.meMax);
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi < lo) return '…';
  return lo === hi ? fmtNum(28000 + lo) : `anything from ${fmtNum(28000 + lo)} to ${fmtNum(28000 + hi)}`;
}

const S = {
  cycleRow  : { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, marginTop: 6 },
  cycleLabel: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--t-37474f)' },
  cycleNote : { margin: '10px 0 0', padding: '10px 14px', background: 'var(--c-fff8e1)', border: '1px solid var(--c-ffe082)', borderRadius: 8, fontSize: 13, color: 'var(--t-5d4037)', lineHeight: 1.6 },
  title: { margin: '0 0 4px', color: 'var(--t-00695c)', fontSize: 18, fontWeight: 700 },
  sub: { margin: '0 0 16px', color: 'var(--t-607d8b)', fontSize: 13, lineHeight: 1.5 },
  muted: { color: 'var(--t-90a4ae)', fontSize: 13 },
  error: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '10px 14px', borderRadius: 8, margin: '10px 0', fontSize: 13 },
  card: { background: 'var(--c-ffffff)', borderRadius: 12, padding: '18px 20px', marginBottom: 16, boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border: '1px solid var(--b-e8f5e9)' },
  cardHead: { display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 12 },
  name: { fontSize: 16, fontWeight: 700, color: 'var(--t-263238)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  badgeBiz: { fontSize: 11, fontWeight: 700, color: 'var(--t-00695c)', background: 'var(--c-e0f2f1)', borderRadius: 20, padding: '2px 9px' },
  badgeInd: { fontSize: 11, fontWeight: 700, color: 'var(--t-6a1b9a)', background: 'var(--c-f3e5f5)', borderRadius: 20, padding: '2px 9px' },
  meta: { fontSize: 13, color: 'var(--t-607d8b)', marginTop: 4, overflowWrap: 'anywhere' },
  lines: { marginBottom: 14 },
  sectionLabel: { fontSize: 12, fontWeight: 700, color: 'var(--t-546e7a)', textTransform: 'uppercase', letterSpacing: '0.4px', margin: '6px 0 6px' },
  line: { fontSize: 14, color: 'var(--t-263238)', padding: '2px 0' },
  lineLabel: { fontSize: 12, color: 'var(--t-78909c)', marginLeft: 6 },
  table: { borderCollapse: 'collapse', marginBottom: 6 },
  th: { textAlign: 'left', fontSize: 12, color: 'var(--t-78909c)', fontWeight: 700, padding: '4px 12px 4px 0' },
  td: { padding: '4px 12px 4px 0', fontSize: 14, color: 'var(--t-333333)', whiteSpace: 'nowrap' },
  input: { padding: '7px 10px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 14, fontFamily: 'inherit', width: 120 },
  grid: { display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 12 },
  field: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 180, maxWidth: 260 },
  fieldLabel: { fontSize: 13, fontWeight: 600, color: 'var(--t-37474f)' },
  hint: { fontSize: 12, color: 'var(--t-90a4ae)' },
  linkBtn: { background: 'none', border: 'none', color: 'var(--t-00897b)', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: '4px 0', fontFamily: 'inherit' },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 },
  cancelBtn: { padding: '8px 16px', background: 'var(--c-ffffff)', color: 'var(--t-546e7a)', border: '1.5px solid var(--c-cfd8dc)', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  picker: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 },
  pickerLabel: { fontSize: 12, fontWeight: 700, color: 'var(--t-546e7a)', textTransform: 'uppercase', letterSpacing: '0.4px' },
  select: { padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--c-cfd8dc)', fontSize: 14, fontFamily: 'inherit', minWidth: 240, maxWidth: '100%', background: 'var(--c-ffffff)' },
  meBox: { marginTop: 18, borderTop: '1px solid var(--c-eceff1)', paddingTop: 14 },
  meToggle: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--t-263238)', cursor: 'pointer' },
  saveBtn: { padding: '8px 20px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'inherit' },
};
